import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, setUpgradeHandler } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Check, Loader2, Rocket } from "lucide-react";

const UpgradeContext = createContext(null);
export const useUpgrade = () => useContext(UpgradeContext);

const PLAN_PITCH = {
  plus: { name: "Plus", price: "$19/mo", perks: ["Unlimited goals, habits & tasks", "300 AI coach messages/mo", "Full AI Academy + advanced Life Score", "Create & join challenges"] },
  pro: { name: "Pro", price: "$39/mo", perks: ["Everything in Plus", "Voice Coaching (120 min/mo)", "Specialized AI coaches + forecasting", "1,500 AI coach messages/mo"] },
};

export function UpgradeProvider({ children }) {
  const [state, setState] = useState(null); // {message, plan}
  useEffect(() => { setUpgradeHandler(setState); return () => setUpgradeHandler(null); }, []);
  return (
    <UpgradeContext.Provider value={{ openUpgrade: setState }}>
      {children}
      <AnimatePresence>{state && <UpgradeModal data={state} onClose={() => setState(null)} />}</AnimatePresence>
    </UpgradeContext.Provider>
  );
}

function UpgradeModal({ data, onClose }) {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [ent, setEnt] = useState(null);
  const [loading, setLoading] = useState(false);
  const plan = PLAN_PITCH[data.plan] || PLAN_PITCH.plus;

  useEffect(() => { api.get("/me/entitlements").then(({ data: e }) => setEnt(e)).catch(() => {}); }, []);

  const canTrial = user && ent && !ent.trial_used && ent.plan === "free";

  const startTrial = async () => {
    setLoading(true);
    try {
      await api.post("/subscriptions/trial", { plan: data.plan });
      await refresh?.();
      toast.success(`Your 7-day ${plan.name} trial has started! Enjoy.`);
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setLoading(false); }
  };

  const seePlans = () => { onClose(); navigate("/pricing"); };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-[#2D3748]/55 p-4 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ scale: 0.9, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 24 }}
        className="relative w-full max-w-md overflow-hidden rounded-[2rem] bg-white p-8 shadow-warm-lg"
        onClick={(e) => e.stopPropagation()} data-testid="upgrade-modal">
        <button onClick={onClose} data-testid="close-upgrade-btn" className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-[#FFF9F5] text-[#718096] hover:bg-[#FFE8E3]"><X className="h-4 w-4" /></button>
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FFF4E6]"><Rocket className="h-6 w-6 text-[#FF7E67]" strokeWidth={2.5} /></span>
        <h3 className="mt-4 font-nunito text-2xl font-extrabold text-[#2D3748]">Unlock with {plan.name}</h3>
        <p className="mt-1 font-quicksand text-sm text-[#718096]">{data.message}</p>

        <div className="mt-5 rounded-2xl bg-[#FFF9F5] p-5">
          <p className="font-nunito text-sm font-bold text-[#B45309]">{plan.name} — {plan.price}</p>
          <ul className="mt-3 space-y-2">
            {plan.perks.map((f) => (
              <li key={f} className="flex items-start gap-2 font-quicksand text-sm text-[#4A5568]"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#06D6A0]" strokeWidth={2.5} />{f}</li>
            ))}
          </ul>
        </div>

        {canTrial ? (
          <button onClick={startTrial} disabled={loading} data-testid="upgrade-trial-btn"
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-sunrise py-3.5 font-nunito font-bold text-white shadow-glow-coral transition-transform duration-200 hover:scale-[1.02] disabled:opacity-70">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Sparkles className="h-4 w-4" strokeWidth={2.5} /> Start 7-day free trial</>}
          </button>
        ) : (
          <button onClick={seePlans} data-testid="upgrade-seeplans-btn"
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-sunrise py-3.5 font-nunito font-bold text-white shadow-glow-coral transition-transform duration-200 hover:scale-[1.02]">
            See all plans
          </button>
        )}
        <button onClick={onClose} className="mt-2 w-full rounded-full py-2 font-quicksand text-sm font-semibold text-[#718096] hover:text-[#2D3748]">Maybe later</button>
      </motion.div>
    </motion.div>
  );
}
